/**
 * Unified price calculation utilities for consistent pricing across all components
 * Ensures chatbot suggestions, cart, and receipts show identical prices
 */

import { menuDataService } from '../services/menuDataService';

/**
 * Get dynamic tax rate from menu data service
 */
export const getDynamicTaxRate = () => {
    try {
        const taxConfig = menuDataService.getTaxConfig();
        if (!taxConfig || !taxConfig.defaults) {
            console.warn('⚠️ Tax config not available, using fallback 0.0825');
            return 0.0825;
        }

        const totalRate = taxConfig.defaults.totalRate;
        if (typeof totalRate !== 'number' || !Number.isFinite(totalRate) || totalRate < 0) {
            console.warn('⚠️ Invalid tax rate, using fallback 0.0825');
            return 0.0825;
        }

        return totalRate;
    } catch (error) {
        console.warn('⚠️ Error getting dynamic tax rate, using fallback:', error);
        return 0.0825;
    }
};

/**
 * Get current menu price for an item (uses cached menu data)
 */
export const getCurrentMenuPrice = (itemId, selectedModifiers = []) => {
    try {
        return menuDataService.calculateCurrentPrice(itemId, selectedModifiers);
    } catch (error) {
        console.warn(`⚠️ Could not get current menu price for ${itemId}:`, error);
        return 0;
    }
};

/**
 * Calculate total price for a single item including all modifiers
 * @param {Object} item - The item object
 * @param {Array} modifiers - Array of modifier objects (optional, uses item.modifiers if not provided)
 * @returns {number} Total price in cents
 */
export const calculateItemTotalPrice = (item, modifiers = null) => {
    if (!item) return 0;

    // Use provided modifiers or fall back to item.modifiers
    const itemModifiers = modifiers || item.modifiers || [];

    // Base price - prefer unitPrice over price
    const basePrice = item.unitPrice != null ? item.unitPrice : (item.price || 0);

    // Calculate modifier total
    const modifierTotal = itemModifiers.reduce((sum, modifier) => {
        const modifierPrice = modifier.unitPrice != null ? modifier.unitPrice : (modifier.price || 0);
        const quantity = modifier.quantity || 1;
        const modifierLineTotal = modifierPrice * quantity;
        console.log(`💰 Modifier calculation: ${modifier.name} = ${modifierPrice} × ${quantity} = ${modifierLineTotal}`);
        return sum + modifierLineTotal;
    }, 0);

    // Return per-unit total (base + modifiers)
    const perUnitTotal = basePrice + modifierTotal;

    // Multiply by item quantity if present
    const itemQuantity = item.quantity || 1;
    const finalTotal = perUnitTotal * itemQuantity;

    console.log(`💰 Item calculation: ${item.name} = (${basePrice} + ${modifierTotal}) × ${itemQuantity} = ${finalTotal}`);

    return finalTotal;
};

/**
 * Calculate line total for an item (handles various data structures)
 * This is the main function that all components should use
 * @param {Object} item - Item object that may have totalPrice, lineSubtotal, or needs calculation
 * @param {boolean} useCurrentPricing - Whether to validate against current menu pricing
 * @returns {number} Total line price in cents
 */
export const getItemLineTotal = (item, useCurrentPricing = false) => {
    if (!item) return 0;

    // Log the item data for debugging
    console.log('💰 getItemLineTotal called with:', {
        itemName: item.name,
        useCurrentPricing,
        totalPrice: item.totalPrice,
        lineSubtotal: item.lineSubtotal,
        price: item.price,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        modifiers: item.modifiers
    });

    // If we already have a computed total price, use it (unless requesting current pricing)
    if (!useCurrentPricing && typeof item.totalPrice === 'number') {
        console.log('💰 Using existing totalPrice:', item.totalPrice);
        return item.totalPrice;
    }

    // If we have lineSubtotal, use it (unless requesting current pricing)
    if (!useCurrentPricing && typeof item.lineSubtotal === 'number') {
        console.log('💰 Using existing lineSubtotal:', item.lineSubtotal);
        return item.lineSubtotal;
    }

    // If requested and item has ID, validate against current menu pricing
    if (useCurrentPricing && item.itemId) {
        try {
            const currentTotal = menuDataService.calculateCurrentPrice(item.itemId, item.modifiers || []);
            console.log('💰 Calculated from cache:', currentTotal);
            return currentTotal;
        } catch (error) {
            console.warn('💰 Failed to get current pricing, falling back:', error);
        }
    }

    // Otherwise calculate from stored data
    const calculatedTotal = calculateItemTotalPrice(item);
    console.log('💰 Calculated from stored data:', calculatedTotal);
    return calculatedTotal;
};

/**
 * Calculate order subtotal from array of items
 * @param {Array} items - Array of order items
 * @returns {number} Subtotal in cents
 */
export const calculateOrderSubtotal = (items) => {
    if (!Array.isArray(items)) return 0;

    return items.reduce((sum, item) => {
        return sum + getItemLineTotal(item);
    }, 0);
};

/**
 * Calculate tax amount
 * @param {number} subtotal - Subtotal in cents
 * @param {number} taxRate - Tax rate (default uses dynamic tax rate from server)
 * @returns {number} Tax amount in cents
 */
export const calculateTax = (subtotal, taxRate = null) => {
    const effectiveRate = taxRate !== null ? taxRate : getDynamicTaxRate();
    return Math.round(subtotal * effectiveRate);
};

/**
 * Calculate order totals (subtotal, tax, total)
 * Uses item-specific tax rates from Clover if available, otherwise falls back to default
 * @param {Array} items - Array of order items (must include taxRateTotal field from menu data)
 * @param {number} taxRate - Tax rate override (optional, for backwards compatibility)
 * @returns {Object} Object with subtotal, tax, and total in cents
 */
export const calculateOrderTotals = (items, taxRate = null) => {
    const subtotal = calculateOrderSubtotal(items);

    // If a specific tax rate is provided, use it for the entire order (legacy behavior)
    if (taxRate !== null) {
        const tax = calculateTax(subtotal, taxRate);
        const total = subtotal + tax;
        return { subtotal, tax, total, taxRate };
    }

    // Calculate tax per-item using Clover-specific rates
    const defaultRate = getDynamicTaxRate();
    let totalTax = 0;

    items.forEach(item => {
        const itemSubtotal = getItemLineTotal(item);
        // Use item-specific tax rate if available, otherwise use default
        const itemTaxRate = item.taxRateTotal ?? defaultRate;
        const itemTax = Math.round(itemSubtotal * itemTaxRate);
        totalTax += itemTax;
    });

    const total = subtotal + totalTax;

    return {
        subtotal,
        tax: totalTax,
        total,
        taxRate: defaultRate // For display purposes, show default rate
    };
};

/**
 * Format price in cents to dollar string
 * @param {number} priceInCents - Price in cents
 * @returns {string} Formatted price string like "$9.17"
 */
export const formatPrice = (priceInCents) => {
    if (typeof priceInCents !== 'number') return '$0.00';
    return `$${(priceInCents / 100).toFixed(2)}`;
};

/**
 * Debug logging for price calculations
 * @param {string} component - Component name for logging
 * @param {Object} item - Item being calculated
 * @param {number} calculatedPrice - The calculated price
 */
export const logPriceCalculation = (component, item, calculatedPrice) => {
    console.log(`💰 [${component}] Price calculation for "${item.name}":`, {
        basePrice: item.unitPrice != null ? item.unitPrice : (item.price || 0),
        modifiers: (item.modifiers || []).map(mod => ({
            name: mod.name,
            price: mod.unitPrice != null ? mod.unitPrice : (mod.price || 0),
            quantity: mod.quantity || 1,
            total: (mod.unitPrice != null ? mod.unitPrice : (mod.price || 0)) * (mod.quantity || 1)
        })),
        quantity: item.quantity || 1,
        calculatedTotal: calculatedPrice,
        formatted: formatPrice(calculatedPrice)
    });
};
